'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import NetruPartner from '@/components/NetruPartner';

const challenges = [
  {
    tag: '[01]',
    title: 'Regulatory Complexity',
    desc: 'FCA, PRA, PCI-DSS, DORA, MiFID II, overlapping obligations with real enforcement teeth. Most firms are managing compliance reactively, not continuously.',
  },
  {
    tag: '[02]',
    title: 'Legacy Infrastructure Risk',
    desc: 'Core banking systems built decades ago sit alongside modern cloud-native services. The attack surface is vast, the blast radius of a breach is enormous.',
  },
  {
    tag: '[03]',
    title: 'Third-Party & Supply Chain Exposure',
    desc: 'Financial services firms rely on hundreds of third-party vendors. DORA mandates ICT risk management across the entire supply chain, not just your own perimeter.',
  },
  {
    tag: '[04]',
    title: 'Operational Resilience Mandates',
    desc: 'The FCA and PRA expect firms to identify important business services, set impact tolerances, and test resilience. This is engineering work, not just policy.',
  },
];

const services = [
  { label: 'DORA Compliance Programme', href: '/services/compliance', desc: 'ICT risk management, incident reporting, resilience testing, and third-party oversight mapped to DORA requirements.' },
  { label: 'PCI-DSS Continuous Compliance', href: '/services/compliance', desc: 'Continuous control monitoring, evidence collection, and cardholder data environment security, not just point-in-time assessments.' },
  { label: 'Accelerator Programme', href: '/services/accelerator', desc: 'Rapid readiness for FCA/PRA audits, regulatory reviews, or investor due diligence. Senior engineers embedded, doing the work at pace.' },
  { label: 'Threat Modelling for FinServ', href: '/services/threat-modelling', desc: 'STRIDE and MITRE ATT&CK applied to payment flows, trading systems, and customer data pipelines before vulnerabilities reach production.' },
  { label: 'AppSec for Financial Applications', href: '/services/appsec', desc: 'SAST, DAST, and SCA embedded into your CI/CD pipelines. Business-logic vulnerabilities in payment and trading systems that scanners miss.' },
  { label: 'Security Focus Programme', href: '/services/security-focus', desc: 'Cut through the regulatory noise. A prioritised, auditor-aligned programme that focuses effort on controls that close real exposure.' },
];

const outcomes = [
  { metric: 'DORA', label: 'Article 30 third-party risk registers built and maintained' },
  { metric: 'PCI-DSS', label: 'Continuous compliance replacing annual point-in-time assessments' },
  { metric: 'FCA', label: 'Operational resilience frameworks mapped to important business services' },
  { metric: 'ISO 27001', label: 'Certification achieved alongside regulatory compliance, one programme' },
];

const PARTNER_HEADING = 'Built to survive a supervisor asking';
const PARTNER_BODY =
  'We engineer the resilience testing, the third-party registers and the detection path that makes a 24 hour reporting clock realistic rather than aspirational. Netru runs the compliance programme alongside it.';

export default function FinancialServicesPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest">Industries</span>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Financial Services</span>
          </div>
          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Financial Services]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security and compliance<br />
              <span className="text-[#80edfa]">built for regulated finance.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              FCA, DORA, PCI-DSS, and operational resilience mandates demand more than policy documents. TechSonar embeds senior security engineers into financial services firms to build continuous compliance, close real exposure, and satisfy regulators, without the unnecessary overhead.
            </p>
          </div>
        </div>
      </section>

      {/* Outcomes strip */}
      <section className="border-t border-[#222] py-12 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {outcomes?.map((o, i) => (
              <div key={i} className="fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-heading text-2xl text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="text-xs text-[#555] leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">The challenge</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              What makes financial services security hard.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#222]">
            {challenges?.map((c, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{c?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{c?.title}</h3>
                <p className="text-sm text-[#666] leading-relaxed">{c?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">How we help</p>
            <h2 className="font-heading text-[clamp(1.8rem,4vw,3rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Services tailored to financial services.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222]">
            {services?.map((s, i) => (
              <Link
                key={i}
                href={s?.href}
                className="group bg-[#0a0f14] p-8 hover:bg-[#111519] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium group-hover:text-[#80edfa] transition-colors pr-4">{s?.label}</h3>
                  <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors flex-shrink-0 mt-1" viewBox="0 0 16 16" fill="none">
                    <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-sm text-[#555] leading-relaxed group-hover:text-[#888] transition-colors">{s?.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-[700px] fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">[Get started]</p>
            <h2 className="font-heading text-[clamp(2rem,4vw,3.5rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
              Ready to close the gap before your next audit?
            </h2>
            <p className="text-[#aaa] text-lg leading-relaxed mb-8">
              Whether you are preparing for a DORA assessment, a PCI-DSS audit, or an FCA review, TechSonar can get you there. Senior engineers, embedded, doing the work.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/contact" className="btn-primary">Book a discovery call</Link>
              <Link href="/services/accelerator" className="px-6 py-3 border border-[#333] text-[#aaa] text-sm uppercase tracking-wide hover:border-[#80edfa] hover:text-[#80edfa] transition-colors font-sans">
                View Accelerator Programme
              </Link>
            </div>
          </div>
        </div>
      </section>

      <NetruPartner

        heading={PARTNER_HEADING}

        body={PARTNER_BODY}

      />


      <Footer />
    </div>
  );
}
